import React, { useEffect, useMemo } from 'react';

import { Select } from '~/eds/src/components/Select/Select';
import { INPUT_DEBOUNCE_MS } from '~/eds/src/constants/duration';
import { useDebounce } from '~/eds/src/hooks/useDebounce';
import { Option } from '~/eds/src/types/Option';
import { RoleType } from '~/enums/Roles';
import { api } from '~/redux/api/createApi';
import { Nullable } from '~/types/Nullable';

type Props = {
  name: string;
  clientId?: string;
  endpoint: keyof typeof api.endpoints;
  value: Nullable<string>;
  onChange: (value: Nullable<string>) => void;
};

export const RolesSelect = ({
  name,
  clientId,
  value,
  onChange,
  ...rest
}: Props) => {
  const [getRoles, { isFetching, data }] =
    api.endpoints.getRolesSimple.useLazyQuery();

  const options = useMemo(() => {
    if (!data?.results) return [];
    return data.results.map((item) => ({
      label: item.name,
      value: item.id,
      description: item.roleType,
    }));
  }, [data?.results]);

  const groupedItems: Array<Option<RoleType, unknown>> = useMemo(
    () =>
      Object.values(
        options.reduce(
          (acc: Record<RoleType, Option>, item) => {
            const { description, label, value } = item;
            if (!acc[description]) {
              acc[description] = {
                label: description,
                options: [],
                value: `${description}-${value}`,
              };
            }
            acc[description].options!.push({ label, value });
            return acc;
          },
          {} as Record<RoleType, Option>,
        ),
      ) as Array<Option<RoleType, unknown>>,
    [options],
  );

  const handleSearch = useDebounce((search: string) => {
    getRoles({
      page: 1,
      pageSize: 100,
      name: search,
      client_id: clientId,
    });
  }, INPUT_DEBOUNCE_MS);

  useEffect(() => {
    getRoles({
      page: 1,
      pageSize: 100,
      name: '',
      client_id: clientId,
    });
  }, [clientId]);

  return (
    <Select
      isLoading={isFetching}
      options={groupedItems}
      name={name}
      value={value}
      onChange={onChange}
      onSearch={handleSearch}
      isMulti={false}
      isSearchable={true}
      {...rest}
    />
  );
};
